import { formatDistanceToNow } from 'date-fns'
import { AlertCircle, ChevronDown, History, Search, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { SkeletonRows } from '@/components/ui/skeleton'
import { useAgentConversations, useDeleteAgentConversation } from '@/hooks/useAgentConversation'
import { cn } from '@/lib/utils'

interface ConversationDropdownProps {
  activeId: string | undefined
  activeTitle: string | undefined
  onSelect: (id: string) => void
  /** Called when the open conversation is deleted, so the drawer can clear it. */
  onDeleted: (id: string) => void
}

/**
 * A panel's thread picker, shared by the staff drawer and the Ask Cai one.
 * Same rows as the standalone page's `ConversationList`, folded into a
 * top-right popover so the panel can give its whole width to the conversation.
 *
 * Which agent's threads it lists comes from `useAgentApi()`, so mounting it
 * under a different `AgentKindProvider` is all it takes to repoint it.
 */
export function ConversationDropdown({
  activeId,
  activeTitle,
  onSelect,
  onDeleted,
}: ConversationDropdownProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const { conversations, isLoading } = useAgentConversations({ q: search.trim() || undefined })
  const deleteMutation = useDeleteAgentConversation()

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    deleteMutation.mutate(id, { onSuccess: () => onDeleted(id) })
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="max-w-56 gap-1.5">
          <History className="size-4 shrink-0" />
          <span className="truncate">{activeTitle || 'Chats'}</span>
          <ChevronDown className="size-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      {/* z-250: this popover opens from inside the agent panel (z-210), so the page-level z-200 would put it behind. */}
      <PopoverContent align="end" className="z-250 w-80 p-0">
        <div className="border-b p-2">
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-2 size-4 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search chats"
              className="pl-8"
            />
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <SkeletonRows className="p-3" rows={5} rowClassName="h-10" />
          ) : conversations.length === 0 ? (
            <div className="text-muted-foreground p-6 text-center text-sm">
              {search ? 'No matches' : 'No chats yet'}
            </div>
          ) : (
            <ul className="divide-y">
              {conversations.map((conv) => (
                <li
                  key={conv.id}
                  onClick={() => {
                    onSelect(conv.id)
                    setOpen(false)
                  }}
                  className={cn(
                    'group hover:bg-muted/50 flex cursor-pointer items-start gap-2 p-3',
                    conv.id === activeId && 'bg-muted'
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {conv.pendingConfirmation && (
                        <AlertCircle
                          className="size-3.5 shrink-0 text-amber-500"
                          aria-label="Pending confirmation"
                        />
                      )}
                      <div className="truncate text-sm font-medium">{conv.title}</div>
                    </div>
                    <div className="text-muted-foreground mt-0.5 text-xs">
                      {formatDistanceToNow(new Date(conv.dateUpdated), { addSuffix: true })}
                    </div>
                  </div>
                  {conv.isOwner && (
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      className="shrink-0 opacity-0 group-hover:opacity-100"
                      onClick={(e) => handleDelete(e, conv.id)}
                      aria-label={`Delete ${conv.title}`}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
